import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { type LogActivity } from "@/api/logActivityService";
import DescriptionIcon from "@mui/icons-material/Description";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";

interface LogActivityTableProps {
  logActivities: LogActivity[];
  count: number;
  isLoading: boolean;
  page: number;
  rowPerPage: number;
  onPageChange: (page: number) => void;
  onRowPerPageChange: (rowPerPage: number) => void;
  handleOpenDetail: (logActivity: LogActivity) => void;
}

const getActionChipProps = (action: string) => {
  const actionLower = action.toLowerCase();

  switch (actionLower) {
    case "create":
      return {
        label: "CREATE",
        color: "success" as const,
        icon: <AddCircleIcon />,
      };
    case "update":
      return {
        label: "UPDATE",
        color: "warning" as const,
        icon: <EditIcon />,
      };
    case "delete":
      return {
        label: "DELETE",
        color: "error" as const,
        icon: <DeleteIcon />,
      };
    case "login":
      return {
        label: "LOGIN",
        color: "info" as const,
        icon: <LoginIcon />,
      };
    case "logout":
      return {
        label: "LOGOUT",
        color: "default" as const,
        icon: <LogoutIcon />,
      };
    default:
      return {
        label: action.toUpperCase(),
        color: "default" as const,
        icon: undefined,
      };
  }
};

const LogActivityTable = ({
  logActivities,
  count,
  isLoading,
  page,
  rowPerPage,
  onPageChange,
  onRowPerPageChange,
  handleOpenDetail,
}: LogActivityTableProps) => {
  const columns: GridColDef<LogActivity>[] = [
    {
      field: "id",
      headerName: "Log ID",
      width: 70,
      headerAlign: "right",
      align: "right",
    },
    { field: "resource", headerName: "หมวดหมู่", width: 150 },
    { field: "entity", headerName: "หมวดหมู่ย่อย", width: 200 },
    {
      field: "performed_by",
      headerName: "ผู้ทำรายการ",
      width: 200,
      flex: 1,
      renderCell: (params) => {
        const email = params.row?.email;
        const role = params.row?.role;
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 0.5,
              alignItems: "center",
              height: "100%",
            }}
          >
            {email ? (
              role ? (
                <>
                  <Chip
                    label={role}
                    color="info"
                    size="small"
                    sx={{ fontWeight: "600", minWidth: 80 }}
                  />
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {" "}
                    {email}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: "0.875rem" }}>{email}</Typography>
              )
            ) : params.value === "system" ? (
              <Chip
                label="system"
                color="error"
                size="small"
                sx={{ fontWeight: "600", minWidth: 80 }}
              />
            ) : (
              <Typography sx={{ fontSize: "0.875rem" }}>
                {params.value}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const chipProps = getActionChipProps(params.value);
        return (
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            icon={chipProps.icon}
            size="small"
            sx={{ fontWeight: "600", minWidth: 80 }}
          />
        );
      },
    },
    {
      field: "time_stamp",
      headerName: "Timestamp",
      width: 140,
      headerAlign: "left",
      align: "left",
      valueFormatter: (params) => {
        const date = new Date(params);
        return date.toLocaleString("th-TH", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      },
    },
    {
      field: "etc",
      headerName: "รายละเอียด",
      width: 100,
      headerClassName: "custom-header",
      headerAlign: "center",
      align: "center",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Tooltip
            title={"รายละเอียดเพิ่มเติม"}
            placement="top"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
            }}
          >
            <IconButton onClick={() => handleOpenDetail(param.row)}>
              <DescriptionIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <DataGrid
        rows={logActivities}
        rowCount={count}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        paginationMode="server"
        paginationModel={{
          page: page,
          pageSize: rowPerPage,
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        sx={{
          minHeight: "50vh",
          maxHeight: "70vh",
          overflow: "auto",
        }}
        onPaginationModelChange={(model) => {
          if (model.page !== page) {
            onPageChange(model.page);
          }
          if (model.pageSize !== rowPerPage) {
            onRowPerPageChange(model.pageSize);
          }
        }}
      />
    </Box>
  );
};

export default LogActivityTable;
